import { useState } from 'react';
import { ClassSchedule, DayOfWeek } from 'app/javascript/types';
import { DISPLAY_DAY_ORDER, DAY_SHORT_LABELS, getCurrentDayOfWeek, trimEmptyEdges, ScheduleHighlight } from 'app/javascript/utils/scheduleHelpers';

const DEFAULT_HIGHLIGHT: ScheduleHighlight = { activeIds: [], nextUpcomingId: null };

interface ScheduleListProps {
  schedules: ClassSchedule[];
  highlight?: ScheduleHighlight;
}

export default function ScheduleList({
  schedules,
  highlight = DEFAULT_HIGHLIGHT,
}: ScheduleListProps) {
  const currentDay = getCurrentDayOfWeek();
  const visibleDays = trimEmptyEdges(DISPLAY_DAY_ORDER, schedules);

  const initialDay = visibleDays.includes(currentDay)
    ? currentDay
    : visibleDays.find((d) => schedules.some((s) => s.day_of_week === d)) ?? currentDay;

  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(initialDay);

  const daySchedules = schedules.filter((s) => s.day_of_week === selectedDay);
  const showHighlight = selectedDay === currentDay;

  return (
    <div>
      <div className="flex gap-1.5 mb-4" role="tablist">
        {visibleDays.map((day) => {
          const hasClasses = schedules.some((s) => s.day_of_week === day);
          const isSelected = day === selectedDay;

          return (
            <button
              key={day}
              id={`tab-${day}`}
              role="tab"
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => setSelectedDay(day)}
              className={`
                flex-1 py-2 text-xs font-semibold rounded-lg transition-all
                ${isSelected
                  ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/30'
                  : hasClasses
                    ? 'bg-slate-800/50 text-slate-300 border border-slate-700/30 hover:border-slate-600/50'
                    : 'bg-slate-900/30 text-slate-600 border border-slate-800/30'
                }
              `}
            >
              {DAY_SHORT_LABELS[day]}
            </button>
          );
        })}
      </div>

      <div className="space-y-2" role="tabpanel" aria-labelledby={`tab-${selectedDay}`}>
        {daySchedules.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">
            Nenhuma aula neste dia.
          </div>
        ) : (
          daySchedules.map((schedule) => {
            const isActive = showHighlight && highlight.activeIds.includes(schedule.id);
            const isNextUpcoming = showHighlight && schedule.id === highlight.nextUpcomingId;
            const isHighlighted = isActive || isNextUpcoming;

            return (
              <div
                key={schedule.id}
                className={`flex items-center gap-4 p-3 rounded-xl ${
                  isHighlighted
                    ? 'bg-yellow-400/5 border border-yellow-400/40'
                    : 'bg-slate-900/50 border border-slate-700/30'
                }`}
              >
                <span className="text-sm font-mono text-yellow-400/80 w-28 flex-shrink-0">
                  {schedule.start_time} - {schedule.end_time}
                </span>
                <span className="text-sm text-white font-medium">{schedule.name}</span>
                {isActive && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400 ml-auto flex-shrink-0">
                    Agora
                  </span>
                )}
                {isNextUpcoming && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-600/40 text-slate-300 ml-auto flex-shrink-0">
                    Próxima
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
